import React, { useCallback, useState } from 'react';

import { useUserPreferences } from '../../user-preferences';
import type {
	ColorAndComponents,
	ColorMode,
	Iconography,
	SectionIds,
	Typography,
	VisualRefreshMode,
} from '../types';

import { useControlledState } from './hooks/use-controlled-state';
import FormSection from './sections';
import ColorModeSection from './sections/color-mode';
import Footer from './sections/footer';
import VisualRefresh from './sections/visual-refresh';

type AppearanceControlsFormProps = {
	colorAndComponents?: ColorAndComponents;
	colorMode?: ColorMode;
	iconography?: Iconography;
	isSynced?: boolean;
	onColorAndComponentsChange?(colorAndComponents: ColorAndComponents): void;
	onColorModeChange?(colorMode: ColorMode): void;
	onIconographyChange?(typography: Iconography): void;
	onSyncedChange?(isSynced: boolean): void;
	onTypographyChange?(typography: Typography): void;
	onVisualRefreshModeChange?(visualRefreshMode: VisualRefreshMode): void;
	typography?: Typography;
	visualRefreshMode?: VisualRefreshMode;
	testId?: string;
	hiddenSections?: SectionIds;
};

/**
 * __Appearance controls form__
 *
 * The form containing fields for example appearance controls.
 */
const AppearanceControlsForm = ({
	colorAndComponents: controlledColorAndComponents,
	colorMode: controlledColorMode,
	iconography: controlledIconography,
	isSynced: controlledIsSynced,
	onColorAndComponentsChange,
	onColorModeChange,
	onIconographyChange,
	onSyncedChange,
	onTypographyChange,
	onVisualRefreshModeChange,
	typography: controlledTypography,
	visualRefreshMode: controlledVisualRefreshMode,
	testId,
	hiddenSections,
}: AppearanceControlsFormProps) => {
	const { userPreferences, setUserPreferences } = useUserPreferences();

	const [uncontrolledIsSynced, setUncontrolledIsSynced] = useState<boolean>(
		userPreferences['exampleSyncSettings'] === 'true' || false,
	);
	const isSynced = controlledIsSynced ?? uncontrolledIsSynced;

	const [colorMode, handleColorModeChange] = useControlledState({
		controlledValue: controlledColorMode,
		defaultValue: 'auto',
		preferenceKey: 'exampleColorMode',
		userPreferences,
		setUserPreferences,
		isSynced,
		onChange: onColorModeChange,
	});

	const [colorAndComponents, handleColorAndComponentsChange] = useControlledState({
		controlledValue: controlledColorAndComponents,
		defaultValue: 'legacy',
		preferenceKey: 'exampleColorAndComponents',
		userPreferences,
		setUserPreferences,
		isSynced,
		onChange: onColorAndComponentsChange,
	});

	const [iconography, handleIconographyChange] = useControlledState({
		controlledValue: controlledIconography,
		defaultValue: 'legacy',
		preferenceKey: 'exampleIconography',
		userPreferences,
		setUserPreferences,
		isSynced,
		onChange: onIconographyChange,
	});

	const [typography, handleTypographyChange] = useControlledState({
		controlledValue: controlledTypography,
		defaultValue: 'adg3',
		preferenceKey: 'exampleTypography',
		userPreferences,
		setUserPreferences,
		isSynced,
		onChange: onTypographyChange,
	});

	const [visualRefreshMode, handleVisualRefreshModeChange] = useControlledState({
		controlledValue: controlledVisualRefreshMode,
		defaultValue: 'legacy',
		preferenceKey: 'exampleVisualRefreshMode',
		userPreferences,
		setUserPreferences,
		isSynced,
		onChange: onVisualRefreshModeChange,
	});

	const handleVisualRefreshModeUpdate = useCallback(
		(mode: VisualRefreshMode) => {
			handleVisualRefreshModeChange(mode);
			if (mode === 'legacy') {
				handleColorAndComponentsChange('legacy');
				handleIconographyChange('legacy');
				handleTypographyChange('adg3');
			} else if (mode === 'refreshed') {
				handleColorAndComponentsChange('refreshed');
				handleIconographyChange('refreshed');
				handleTypographyChange('refreshed');
			}
		},
		[
			handleVisualRefreshModeChange,
			handleColorAndComponentsChange,
			handleIconographyChange,
			handleTypographyChange,
		],
	);

	const handleSyncedChange = useCallback(
		(isSynced: boolean) => {
			onSyncedChange?.(isSynced);
			if (!controlledIsSynced) {
				setUncontrolledIsSynced(isSynced);
			}

			if (isSynced) {
				setUserPreferences({
					exampleSyncSettings: 'true',
					exampleColorMode: colorMode,
					exampleColorAndComponents: colorAndComponents,
					exampleIconography: iconography,
					exampleTypography: typography,
					exampleVisualRefreshMode: visualRefreshMode,
				});
			} else {
				// Wipe example user preferences
				setUserPreferences({
					exampleSyncSettings: 'false',
					exampleColorMode: null,
					exampleColorAndComponents: null,
					exampleIconography: null,
					exampleTypography: null,
					exampleVisualRefreshMode: null,
				});
			}
		},
		[
			onSyncedChange,
			controlledIsSynced,
			setUserPreferences,
			colorMode,
			colorAndComponents,
			iconography,
			typography,
			visualRefreshMode,
		],
	);

	return (
		<div data-testid={testId}>
			<FormSection hasBorder>
				<ColorModeSection
					colorMode={colorMode}
					onColorModeChange={handleColorModeChange}
					testId={testId ? `${testId}--color-mode-section` : undefined}
				/>
			</FormSection>
			<FormSection hasBorder>
				<VisualRefresh
					colorAndComponents={colorAndComponents}
					iconography={iconography}
					onColorAndComponentsChange={handleColorAndComponentsChange}
					onIconographyChange={handleIconographyChange}
					onTypographyChange={handleTypographyChange}
					typography={typography}
					visualRefreshMode={visualRefreshMode}
					onVisualRefreshModeChange={handleVisualRefreshModeUpdate}
					testId={testId ? `${testId}--visual-refresh-section` : undefined}
					hiddenSections={hiddenSections}
				/>
			</FormSection>
			<FormSection>
				<Footer
					isSynced={isSynced}
					onSyncedChange={handleSyncedChange}
					testId={testId ? `${testId}--footer` : undefined}
				/>
			</FormSection>
		</div>
	);
};

export default AppearanceControlsForm;
